import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { getAllWordsFromIDB, getAllProgress, isInitialized } from '../db';
import { triggerAutoSync, subscribeSyncStatus } from '../sync';
import { logout } from '../auth';
import './Home.css';

function Home() {
    const [progressData, setProgressData] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const [stats, setStats] = useState({ pictures_count: 0, words_count: 0 });
    const [needInit, setNeedInit] = useState(false);   // 首次离线: 需联网初始化
    const [blocking, setBlocking] = useState(false);   // 同步中且本地有 dirty: 禁用学习入口
    const navigate = useNavigate();
    const isOnline = useOnlineStatus();

    // 离线降级: 从 IndexedDB 构造与 /api/home/process 同格式的数据 (emoji 字符串)
    // split: 以 new_group_id(拆分组) 为行单位
    const buildOfflineData = async () => {
        const allWords = await getAllWordsFromIDB();
        const progress = await getAllProgress();
        const completedByGroup = {};
        progress.forEach(p => {
            const g = String(p.new_group_id);
            if (!completedByGroup[g]) completedByGroup[g] = {};
            completedByGroup[g][p.op_type] = p.completed_at;
        });
        const allGroups = Array.from(new Set(allWords.map(w => String(w.new_group_id))))
            .filter(Boolean)
            .sort((a, b) => Number(a) - Number(b));
        let firstUnstudied = null;
        for (const g of allGroups) {
            if (!completedByGroup[g] || !completedByGroup[g]['study']) { firstUnstudied = g; break; }
        }
        const OPS = ['study', 'test', 'review1', 'review2', 'review3'];
        const PREV = { test: 'study', review1: 'test', review2: 'review1', review3: 'review2' };
        const progressData = allGroups.map(g => {
            const p = completedByGroup[g] || {};
            const row = { group: `Group ${g}` };
            for (const op of OPS) {
                if (p[op]) { row[op] = `✔️ ${p[op].slice(0, 10)}`; continue; }
                if (op === 'study') { row[op] = (g === firstUnstudied) ? '✅请开始学习' : ''; continue; }
                if (op === 'test') { row[op] = p['study'] ? '✅请开始测试' : ''; continue; }
                row[op] = p[PREV[op]] ? '✅请开始复习' : '';
            }
            return row;
        });
        return {
            stats: { pictures_count: 0, words_count: allWords.length },
            progressData,
        };
    };

    // 从本地 IndexedDB 渲染首页 (永远本地优先, 不读云端进度)
    const renderLocal = async () => {
        const d = await buildOfflineData();
        setProgressData(d.progressData);
        setStats(d.stats);
        return d.progressData.length;
    };

    useEffect(() => {
        (async () => {
            const init = await isInitialized();
            // 首次离线: 不能进入学习, 提示需联网
            if (!isOnline && !init) {
                setNeedInit(true);
                setProgressData([]);
                return;
            }
            setNeedInit(false);
            await renderLocal();
            // 在线: 后台自动同步 (首次在线会 warmup 词库+进度; 已初始化则合并云端)
            if (isOnline) triggerAutoSync();
        })();
        if (!isOnline) return;
        // 在线时定时自动合并其他设备的进度
        const timer = setInterval(() => triggerAutoSync(), 30000);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOnline]);

    // 订阅同步状态: 同步完成重渲染本地; 同步中且有 dirty 时禁用学习入口
    useEffect(() => {
        return subscribeSyncStatus((s) => {
            if (s.state === 'synced') renderLocal();
            setBlocking(s.state === 'syncing' && s.detail && s.detail.dirty > 0);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredData = progressData.filter(item => {
        if (showAll) return true;
        const isDone = [item.study, item.test, item.review1, item.review2, item.review3]
            .every(val => val && val.includes('✔️'));
        return !isDone;
    });

    const getStatusClass = (text) => {
        if (!text) return '';
        if (text.includes('✔️')) return 'status-done';
        if (text.includes('✅')) return 'status-start';
        if (text.includes('⚠️')) return 'status-warning';
        return '';
    };

    const handleCellClick = (item, type) => {
        const groupId = item.group.replace('Group ', '');
        if (type === 'group') {
            navigate(`/group/${groupId}`);
            return;
        }
        // 同步中且本地有 dirty: 禁止开始新的学习/测试/复习 (先同步完成)
        if (blocking) {
            alert('同步完成后再开始新的学习');
            return;
        }
        if (type === 'study' && item.study && item.study.includes('请开始学习')) {
            navigate(`/study/${groupId}`);
        }
        if (type === 'test' && item.test && item.test.includes('请开始测试')) {
            navigate(`/test/${groupId}?type=test`);
        }
        if (type === 'review1' && item.review1 && (item.review1.includes('请开始复习') || item.review1.includes('请尽快复习'))) {
            navigate(`/test/${groupId}?type=review1`);
        }
        if (type === 'review2' && item.review2 && (item.review2.includes('请开始复习') || item.review2.includes('请尽快复习'))) {
            navigate(`/test/${groupId}?type=review2`);
        }
        if (type === 'review3' && item.review3 && (item.review3.includes('请开始复习') || item.review3.includes('请尽快复习'))) {
            navigate(`/test/${groupId}?type=review3`);
        }
    };

    return (
        <div className="main-app">
            <header className="header">
                <h1>单词学习进度记录</h1>
                <div className="header-right">
                    <p className="stats-info">
                        图片总数: {stats.pictures_count} | 单词总数: {stats.words_count}
                    </p>
                    <button className="logout-btn" onClick={() => { logout(); window.location.reload(); }}>退出</button>
                </div>
            </header>

            {needInit && (
                <div className="init-block">
                    <p>首次使用需要联网初始化一次，请连接网络后重新打开。</p>
                </div>
            )}

            {blocking && (
                <div className="sync-block-tip">同步完成后再开始新的学习</div>
            )}

            <div className="action-bar">
                <button className="toggle-btn" onClick={() => setShowAll(!showAll)}>
                    {showAll ? "➖ 隐藏已完成组" : "➕ 显示已完成的组"}
                </button>
                <button className="toggle-btn" onClick={() => navigate('/calendar')}>
                    📅 学习日历
                </button>
            </div>

            <div className="table-container">
                <table className="progress-table">
                    <thead>
                        <tr>
                            <th>组别</th>
                            <th>基础学习</th>
                            <th>单元测试</th>
                            <th>复习 I</th>
                            <th>复习 II</th>
                            <th>复习 III</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.map((item, index) => {
                            const ng = item.group.replace('Group ', '');
                            const isSplit = ng.includes('.');
                            return (
                            <tr key={index}>
                                <td
                                    className={`group-name${isSplit ? ' group-name-split' : ''}`}
                                    onClick={() => handleCellClick(item, 'group')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {item.group}
                                </td>
                                <td 
                                    className={getStatusClass(item.study)}
                                    onClick={() => handleCellClick(item, 'study')}
                                    style={{ cursor: item.study.includes('请开始学习') ? 'pointer' : 'default' }}
                                >
                                    {item.study}
                                </td>
                                <td
                                    className={getStatusClass(item.test)}
                                    onClick={() => handleCellClick(item, 'test')}
                                    style={{ cursor: item.test && item.test.includes('请开始') ? 'pointer' : 'default' }}
                                >
                                    {item.test}
                                </td>
                                <td
                                    className={getStatusClass(item.review1)}
                                    onClick={() => handleCellClick(item, 'review1')}
                                    style={{ cursor: item.review1 && (item.review1.includes('请开始') || item.review1.includes('请尽快')) ? 'pointer' : 'default' }}
                                >
                                    {item.review1}
                                </td>
                                <td
                                    className={getStatusClass(item.review2)}
                                    onClick={() => handleCellClick(item, 'review2')}
                                    style={{ cursor: item.review2 && (item.review2.includes('请开始') || item.review2.includes('请尽快')) ? 'pointer' : 'default' }}
                                >
                                    {item.review2}
                                </td>
                                <td
                                    className={getStatusClass(item.review3)}
                                    onClick={() => handleCellClick(item, 'review3')}
                                    style={{ cursor: item.review3 && (item.review3.includes('请开始') || item.review3.includes('请尽快')) ? 'pointer' : 'default' }}
                                >
                                    {item.review3}
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <footer className="footer">
                <button className="error-correction-btn">进入消错模式</button>
            </footer>
        </div>
    );
}

export default Home;
